
import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { Link } from "react-router-dom"
import { IreneLogo } from "@/components/irene-logo"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Card, CardContent } from "@/components/ui/card"
import { ArrowRight, ArrowLeft, Check, Loader2 } from "lucide-react"
import { cn } from "@/lib/utils"
import { useAuth } from "@/lib/auth-context"

interface OnboardingData {
  companyName: string
  founderName: string
  email: string
  password: string
  role: string
  industry: string
  stage: string
  pitch: string
  mrr: string
  runway: string
  teamSize: string
  targetRaise: string
  investorPreferences: string[]
  priorities: string[]
}

const STEPS = [
  { title: "About You", subtitle: "Tell Irene who you are" },
  { title: "Your Company", subtitle: "Help Irene understand your business" },
  { title: "Fundraising", subtitle: "Where are you in your raise?" },
  { title: "Preferences", subtitle: "Customize your experience" },
]

const INDUSTRIES = [
  "AI / Machine Learning",
  "B2B SaaS",
  "Consumer",
  "FinTech",
  "HealthTech",
  "EdTech",
  "Climate / CleanTech",
  "E-Commerce / Marketplace",
  "Developer Tools",
  "Web3 / Blockchain",
  "Other",
]

const STAGES = [
  "Pre-Seed",
  "Seed",
  "Series A",
  "Series B",
  "Series C+",
]

const INVESTOR_PREFS = [
  "Data-driven (metrics focused)",
  "Vision-focused (big picture)",
  "Milestone-based (progress tracking)",
  "Hands-on operators",
  "Strategic value-add",
  "Silent / passive",
]

const PRIORITIES = [
  "Finding new investors",
  "Managing existing relationships",
  "Sending investor updates",
  "Preparing pitch decks",
  "Due diligence prep",
  "Deal room management",
]

export default function OnboardingPage() {
  const router = useNavigate()
  const { register } = useAuth()
  const [step, setStep] = useState(0)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState("")
  const [data, setData] = useState<OnboardingData>({
    companyName: "",
    founderName: "",
    email: "",
    password: "",
    role: "",
    industry: "",
    stage: "",
    pitch: "",
    mrr: "",
    runway: "",
    teamSize: "",
    targetRaise: "",
    investorPreferences: [],
    priorities: [],
  })

  const updateField = (field: keyof OnboardingData, value: string) => {
    setData((prev) => ({ ...prev, [field]: value }))
  }

  const toggleArrayField = (field: "investorPreferences" | "priorities", value: string) => {
    setData((prev) => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter((v) => v !== value)
        : [...prev[field], value],
    }))
  }

  const canProceed = () => {
    switch (step) {
      case 0:
        return data.founderName.trim() !== "" && data.email.trim() !== "" && data.password.trim().length >= 8
      case 1:
        return data.companyName.trim() !== "" && data.industry !== "" && data.stage !== ""
      case 2:
        return true
      case 3:
        return true
      default:
        return false
    }
  }

  const handleFinish = async () => {
    setError("")
    setIsSubmitting(true)
    try {
      await register({
        email: data.email,
        password: data.password,
        name: data.founderName,
        company_name: data.companyName || undefined,
        company_stage: data.stage || undefined,
        industry: data.industry || undefined,
      })
      // Redirect to Stripe trial page instead of dashboard
      router("/stripe-trial")
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Registration failed. Please try again."
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <div className="w-full max-w-xl">
        {/* Header */}
        <div className="mb-8 flex flex-col items-center text-center">
          <IreneLogo size={56} className="mb-4" />
          <h1 className="text-3xl font-bold text-foreground">
            Welcome to Irene
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Your AI-powered investor relations assistant
          </p>
        </div>

        {/* Step indicators */}
        <div className="mb-8 flex items-center justify-center gap-2">
          {STEPS.map((s, i) => (
            <div key={s.title} className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => i < step && setStep(i)}
                className={cn(
                  "flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold transition-colors",
                  i < step
                    ? "bg-primary text-primary-foreground cursor-pointer"
                    : i === step
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground"
                )}
              >
                {i < step ? <Check className="h-4 w-4" /> : i + 1}
              </button>
              {i < STEPS.length - 1 && (
                <div
                  className={cn(
                    "h-0.5 w-8 rounded-full transition-colors",
                    i < step ? "bg-primary" : "bg-muted"
                  )}
                />
              )}
            </div>
          ))}
        </div>

        {/* Step title */}
        <div className="mb-6 text-center">
          <h2 className="text-xl font-semibold text-foreground">
            {STEPS[step].title}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {STEPS[step].subtitle}
          </p>
        </div>

        {/* Step content */}
        <Card className="border-border shadow-sm">
          <CardContent className="p-6">
            {/* Step 0: About You */}
            {step === 0 && (
              <div className="flex flex-col gap-5">
                <div className="flex flex-col gap-2">
                  <Label htmlFor="founderName">Your name</Label>
                  <Input
                    id="founderName"
                    placeholder="Jane Doe"
                    value={data.founderName}
                    onChange={(e) => updateField("founderName", e.target.value)}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="email">Email address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="jane@heysalad.com"
                    value={data.email}
                    onChange={(e) => updateField("email", e.target.value)}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="Min. 8 characters"
                    value={data.password}
                    onChange={(e) => updateField("password", e.target.value)}
                    minLength={8}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="role">Your role</Label>
                  <Select value={data.role} onValueChange={(v) => updateField("role", v)}>
                    <SelectTrigger id="role">
                      <SelectValue placeholder="Select your role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ceo">CEO / Co-founder</SelectItem>
                      <SelectItem value="cto">CTO / Technical Co-founder</SelectItem>
                      <SelectItem value="cfo">CFO / Finance Lead</SelectItem>
                      <SelectItem value="head-fundraising">Head of Fundraising</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Step 1: Your Company */}
            {step === 1 && (
              <div className="flex flex-col gap-5">
                <div className="flex flex-col gap-2">
                  <Label htmlFor="companyName">Company name</Label>
                  <Input
                    id="companyName"
                    placeholder="HeySalad"
                    value={data.companyName}
                    onChange={(e) => updateField("companyName", e.target.value)}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="industry">Industry</Label>
                  <Select value={data.industry} onValueChange={(v) => updateField("industry", v)}>
                    <SelectTrigger id="industry">
                      <SelectValue placeholder="Select your industry" />
                    </SelectTrigger>
                    <SelectContent>
                      {INDUSTRIES.map((ind) => (
                        <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="stage">Fundraising stage</Label>
                  <Select value={data.stage} onValueChange={(v) => updateField("stage", v)}>
                    <SelectTrigger id="stage">
                      <SelectValue placeholder="Select current stage" />
                    </SelectTrigger>
                    <SelectContent>
                      {STAGES.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="pitch">One-line pitch</Label>
                  <Textarea
                    id="pitch"
                    placeholder="We help busy professionals eat healthier by delivering personalized salad boxes..."
                    value={data.pitch}
                    onChange={(e) => updateField("pitch", e.target.value)}
                    rows={3}
                  />
                </div>
              </div>
            )}

            {/* Step 2: Fundraising */}
            {step === 2 && (
              <div className="flex flex-col gap-5">
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="mrr">Current MRR</Label>
                    <Input
                      id="mrr"
                      placeholder="$15,000"
                      value={data.mrr}
                      onChange={(e) => updateField("mrr", e.target.value)}
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="runway">Runway (months)</Label>
                    <Input
                      id="runway"
                      placeholder="18"
                      value={data.runway}
                      onChange={(e) => updateField("runway", e.target.value)}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="teamSize">Team size</Label>
                    <Input
                      id="teamSize"
                      placeholder="8"
                      value={data.teamSize}
                      onChange={(e) => updateField("teamSize", e.target.value)}
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="targetRaise">Target raise</Label>
                    <Input
                      id="targetRaise"
                      placeholder="$2M"
                      value={data.targetRaise}
                      onChange={(e) => updateField("targetRaise", e.target.value)}
                    />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  These numbers help Irene personalize your investor communications.
                  You can always update them later in Settings.
                </p>
              </div>
            )}

            {/* Step 3: Preferences */}
            {step === 3 && (
              <div className="flex flex-col gap-6">
                <div className="flex flex-col gap-3">
                  <Label>What type of investors are you looking for?</Label>
                  <div className="flex flex-wrap gap-2">
                    {INVESTOR_PREFS.map((pref) => (
                      <button
                        key={pref}
                        type="button"
                        onClick={() => toggleArrayField("investorPreferences", pref)}
                        className={cn(
                          "rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
                          data.investorPreferences.includes(pref)
                            ? "border-primary bg-primary text-primary-foreground"
                            : "border-border bg-card text-muted-foreground hover:border-primary/50 hover:text-foreground"
                        )}
                      >
                        {pref}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex flex-col gap-3">
                  <Label>What are your top priorities?</Label>
                  <div className="flex flex-wrap gap-2">
                    {PRIORITIES.map((p) => (
                      <button
                        key={p}
                        type="button"
                        onClick={() => toggleArrayField("priorities", p)}
                        className={cn(
                          "rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
                          data.priorities.includes(p)
                            ? "border-primary bg-primary text-primary-foreground"
                            : "border-border bg-card text-muted-foreground hover:border-primary/50 hover:text-foreground"
                        )}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Navigation buttons */}
        <div className="mt-6 flex items-center justify-between">
          <Button
            variant="outline"
            onClick={() => setStep((s) => s - 1)}
            disabled={step === 0}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>

          {step < STEPS.length - 1 ? (
            <Button
              onClick={() => setStep((s) => s + 1)}
              disabled={!canProceed()}
              className="gap-2"
            >
              Continue
              <ArrowRight className="h-4 w-4" />
            </Button>
          ) : (
            <Button onClick={handleFinish} disabled={isSubmitting} className="gap-2">
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Creating account...
                </>
              ) : (
                <>
                  Launch Irene
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </Button>
          )}
        </div>

        {error && (
          <p className="mt-4 rounded-lg bg-destructive/10 px-3 py-2 text-center text-sm text-destructive">
            {error}
          </p>
        )}

        <p className="mt-6 text-center text-sm text-muted-foreground">
          Already have an account?{" "}
          <Link
            to="/login"
            className="font-medium text-primary hover:underline"
          >
            Sign in
          </Link>
        </p>
      </div>
    </div>
  )
}
